import { CheckCircle, MoreVert, Schedule, Warning } from "@mui/icons-material";

import { useState, useRef } from "react";
import { NodeExecutionCondition, WorkflowNode } from "../../types/workflow";
import WorkflowBoxMenu from "./WorkflowBoxMenu";
import { useUpdateNodeName } from "../../api/nodeApi";
import { WorkflowServiceIcon } from "../Common/WorkflowServiceIcon";

interface WorkflowBoxHeaderProps {
    workflowType: string;
    workflowBoxIndex: number;
    workflowTitle?: string;
    chosenAction?: string;
    nodeConfigs?: Record<string, any>;
    nodeId?: string;
    integrations?: any[];
    onHeaderClick: () => void;
    workflowNode: WorkflowNode;
    deleteNodeById: (params: { nodeId: string }) => void;
    shouldDeleteNode: () => void;
    localConditions: Record<string, any>;
    refreshWorkflowNodes: () => void;
    getHasChildren: (node: WorkflowNode) => boolean;
    hideMenu?: boolean;
}

const skipIntegration = (workflowType: string) => {
    return ["webhook", "sesinbound", "delay", "filter", "code", "chatgpt", "postgres", "paths", "nanonets", "pdf", "schedule", "scraper", "review", "browserAIAgent", "DynamicWorkflowAgent", "approveit", "sftp", "files", "salesforce"].indexOf(workflowType) !== -1;
}

const WorkflowBoxHeader = ({
    workflowType,
    workflowBoxIndex,
    workflowTitle,
    chosenAction,
    nodeConfigs,
    nodeId,
    integrations,
    onHeaderClick,
    workflowNode,
    deleteNodeById,
    shouldDeleteNode,
    localConditions,
    refreshWorkflowNodes,
    getHasChildren,
    hideMenu,
}: WorkflowBoxHeaderProps) => {
    // Compute values internally
    const isIntegrationPresent = Array.isArray(integrations) && 
        integrations.some(integration => integration.service_id === workflowType);

    const nodeRunConfig = nodeConfigs?.[nodeId || '']?.run_config || {};
    const actionMetadata = nodeConfigs?.[nodeId || '']?.action_metadata || {};
    const nodeRunConfigHasKeys = !!Object.keys(nodeRunConfig)?.length;
    const isTriggerAdded = nodeRunConfigHasKeys && 
        Object.values(nodeRunConfig).some(value => value && String(value)?.trim());

    const [menuAnchorEl, setMenuAnchorEl] = useState<SVGSVGElement | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState(workflowTitle || '');
    const isRenamingFromMenu = useRef(false);
    
    const updateNodeName = useUpdateNodeName();

    const handleTitleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (editedTitle.trim() && nodeId) {
            updateNodeName.mutate(
                { nodeId, name: editedTitle },
                {
                    onSuccess: () => {
                        setIsEditing(false);
                        refreshWorkflowNodes();
                    }
                }
            );
        }
    };

    const startRename = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        isRenamingFromMenu.current = true;
        setIsEditing(true);
        setEditedTitle(workflowTitle || '');
        // Reset the flag after a short delay
        setTimeout(() => {
            isRenamingFromMenu.current = false;
        }, 100);
    };

    /**
     * A node is incomplete if:
     * 1. No action chosen
     * 2. Integration (oauth) required and no integration present
     * 3. Run config isn't complete
     * 4. Execution conditions aren't present in case of filter node
     * 5. Test run instance isn't present if node is action node
     * 6. Run instance isn't present if node is trigger node
     */
    const isAnyStepIncomplete = Boolean(
        !chosenAction ||
        (workflowType && !skipIntegration(workflowType) && !isIntegrationPresent) ||
        (nodeRunConfigHasKeys && !isTriggerAdded && workflowType !== 'filter') ||
        (workflowType == 'filter' && (!localConditions || !localConditions.some((cond: NodeExecutionCondition) => !!cond.id)))
    );

    const hasChildren = getHasChildren(workflowNode);

    const isPollingNode = actionMetadata?.internal_node_type === 2;
    const pollingInterval = Math.floor(actionMetadata?.run_interval / 60);

    return (
        <div 
            className="p-3 flex justify-between cursor-pointer"
            onClick={onHeaderClick}
        >
            <div className="flex gap-3 items-center">
                <WorkflowServiceIcon
                    workflowType={workflowType}
                    alt={workflowType}
                    height={20}
                    width={20}
                />
                {isEditing ? (
                    <form 
                        onSubmit={handleTitleSubmit} 
                        className="flex items-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <input
                            type="text"
                            value={editedTitle}
                            onChange={(e) => setEditedTitle(e.target.value)}
                            className="border border-solid border-borderGrey rounded px-2 py-1"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                    setIsEditing(false);
                                }
                            }}
                            onBlur={(e) => {
                                if (!isRenamingFromMenu.current) {
                                    handleTitleSubmit(e as unknown as React.FormEvent);
                                }
                            }}
                        />
                    </form>
                ) : (
                    <div 
                        className="capitalize text-sm"
                        onDoubleClick={() => {
                            setIsEditing(true);
                            setEditedTitle(workflowTitle || '');
                        }}
                    >
                        {workflowBoxIndex}{". "}{workflowTitle}
                    </div>
                )}
            </div>
            <div className="flex gap-1 items-center" onClick={(e) => e.stopPropagation()}>
                {isPollingNode && (
                    <div className="flex items-center gap-1 text-xs text-greyishBlue">
                        <Schedule fontSize="small" />
                        {pollingInterval} min
                    </div>
                )}
                {/* {isAnyStepIncomplete && (
                    <>
                        <Processing />
                        <div className="text-orange text-xs">
                            {(isIntegrationPresent && chosenAction) || (isTriggerAdded && chosenAction) ? '1/3' : chosenAction ? '2/3' : '3/3'} actions required
                        </div>
                    </>
                )} */}
                {isAnyStepIncomplete ? (
                    <Warning className="text-orange" fontSize="small" />
                ) : (
                    <CheckCircle className="text-green" fontSize="small" />
                )}
                {!hideMenu ? (
                    <MoreVert 
                        className="text-greyishBlue" 
                        fontSize="small" 
                        onClick={(event) => {
                            event.stopPropagation();
                            setMenuAnchorEl(event.currentTarget as SVGSVGElement);
                        }} 
                    />
                ) : null}
            </div>
            <WorkflowBoxMenu
                menuAnchorEl={menuAnchorEl}
                setMenuAnchorEl={setMenuAnchorEl}
                workflowNode={workflowNode}
                deleteNodeById={deleteNodeById}
                shouldDeleteNode={shouldDeleteNode}
                refreshWorkflowNodes={refreshWorkflowNodes}
                hasChildren={hasChildren}
                onStartRename={startRename}
            />
        </div>
    );
};

export default WorkflowBoxHeader; 